import { cn } from '../../lib/cn.ts';

export interface ToastProps {
  open: boolean;
  /* One line. A toast that needs two sentences is a dialog wearing the wrong clothes. */
  message: string;
  /* The way back, when the action that raised this can be taken back. */
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
}

/**
 * A line at the foot of the screen saying what just happened, with the way back
 * beside it.
 *
 * It carries no timer and no queue of its own: how long a message stands, and
 * what replaces it, belongs to the page that raised it. The library only knows
 * what one looks like.
 *
 * It stays in the document while closed rather than being mounted on demand.
 * The live region has to exist before the text lands in it, or a screen reader
 * has nothing to announce a change against — which is the whole point of a
 * message that never takes focus.
 */
export function Toast({ open, message, actionLabel, onAction, className }: ToastProps) {
  return (
    <div
      data-slot='toast'
      data-open={open}
      role='status'
      aria-live='polite'
      className={cn(
        /* Over the drawers on the right edge, under the dialogs and menus at 50. */
        'fixed bottom-6.5 left-1/2 z-40 flex -translate-x-1/2 items-center gap-4',
        'rounded-full bg-primary px-4.5 py-3 text-sm text-white shadow-raise transition',
        open ? 'visible opacity-100' : 'invisible translate-y-2.5 opacity-0',
        className,
      )}
    >
      <span>{open ? message : ''}</span>
      {actionLabel && onAction && (
        <button
          type='button'
          onClick={onAction}
          className='shrink-0 cursor-pointer text-sm font-semibold text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
